import { useQuery, useMutation } from 'react-query'
import {
  EditCapacityTableDataType,
  UpdateWorkcentreCapacityParamsType,
  UpdateWorkcentreConstrainedPlanParams,
  WorkcentreCapacityParams,
} from './EditCapacity.types'
import { getApiErrorMessage, request } from 'Features/auth/axios-client'
import { WORKCENTRE_STATUS_FETCH_TIMEOUT } from 'Common/Constants/Constants'

const fetchWorkcentreRunStatus = async (params: WorkcentreCapacityParams) => {
  const { planId, sourcesiteId, workcentreId } = params
  const data = await request({
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/run-status`,
  })
  return data
}

export const useWorkcentreRunStatus = (params: WorkcentreCapacityParams) => {
  return useQuery(
    [
      'workcentre-run-status',
      params.planId,
      params.sourcesiteId,
      params.workcentreId,
    ],
    () => fetchWorkcentreRunStatus(params),
    {
      cacheTime: 0,
      select: (response) => response?.data?.data,
      refetchOnWindowFocus: true,
      refetchInterval: WORKCENTRE_STATUS_FETCH_TIMEOUT,
    }
  )
}

const fetchWorkcentreCapacityData = async (
  params: WorkcentreCapacityParams
) => {
  const { planId, sourcesiteId, workcentreId } = params
  const data = await request({
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/capacity/edit-capacity`,
  })
  return data
}

export const useWorkcentreCapacityData = (
  params: WorkcentreCapacityParams,
  enabled = false
) => {
  return useQuery(
    [
      'workcentre-capacity-data',
      params.planId,
      params.sourcesiteId,
      params.workcentreId,
    ],
    () => fetchWorkcentreCapacityData(params),
    {
      cacheTime: 0,
      select: (response) => {
        const { data } = response?.data
        return data.map(
          (capacityData: EditCapacityTableDataType, index: number) => {
            //This is a work around to have an id in the objects so that mui table works.
            return { ...capacityData, id: index }
          }
        )
      },
      refetchOnWindowFocus: false,
      enabled: enabled,
    }
  )
}

const updateWorkcentreConstrainedPlan = async ({
  planId,
  sourcesiteId,
  workcentreId,
}: UpdateWorkcentreConstrainedPlanParams) => {
  const { data } = await request({
    method: 'put',
    data: {},
    url: `plan/${planId}/source_sites/${sourcesiteId}/workcentres/${workcentreId}/capacity/update-plan`,
  })
  return data
}

export const useUpdateConstrainedPlan = (
  onSucces: () => void,
  onError: (message: string) => void
) => {
  return useMutation(updateWorkcentreConstrainedPlan, {
    onSuccess: () => {
      onSucces()
    },
    onError: (error) => {
      onError(getApiErrorMessage(error, 'Unable to initiate the plan run.'))
    },
  })
}

const updateWorkcentreCapacityData = async ({
  planId,
  sourcesiteId,
  workcentreId,
  updatedData,
}: UpdateWorkcentreCapacityParamsType) => {
  const data = await request({
    method: 'put',
    data: { updatedData },
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/capacity/edit-capacity`,
  })
  return data
}

export const useUpdateWorkCentreCapacityData = (
  onSuccess: () => void,
  onError: () => void
) => {
  return useMutation(updateWorkcentreCapacityData, {
    onSuccess: () => {
      onSuccess()
    },
    onError: () => {
      onError()
    },
  })
}
